import { isGlobalViewer } from '../../../constants/roles';
// src/modules/treasury/pages/BranchFundingHistory.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Typography, Spin, Alert, Row, Col, DatePicker, Select, Tooltip, Button } from 'antd';
import { FileTextOutlined, ReloadOutlined, LockOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../../context/AuthContext';
import { fetchBranchById, fetchBranches } from '../../branches/api';
import { fetchCentralFund } from '../api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');

export default function BranchFundingHistory() {
  const { user } = useAuth();
  const [isHO, setIsHO] = useState(false);
  const [initLoading, setInitLoading] = useState(true);

  const [entries, setEntries] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });
  const [filters, setFilters] = useState({ dateRange: null, branchId: null });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);

  // Init
  useEffect(() => {
    (async () => {
      try {
        if (!user?.branch) { setInitLoading(false); return; }
        const branchRes = await fetchBranchById(user.branch);
        const branch = branchRes?.data || branchRes;
        const ho = branch?.branchType === 'HO' || isGlobalViewer(user);
        setIsHO(ho);

        if (ho) {
          setBranchesLoading(true);
          const brRes = await fetchBranches({ limit: 1000 });
          setBranches(brRes?.data?.data || brRes?.data || []);
          setBranchesLoading(false);
        }
      } catch {
        setError('Failed to load profile');
      } finally {
        setInitLoading(false);
      }
    })();
  }, [user]);

  const fetchHistory = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: pagination.pageSize, entryType: 'BRANCH_FUNDING' };
      if (filters.dateRange?.[0]) params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
      if (filters.dateRange?.[1]) params.endDate = filters.dateRange[1].format('YYYY-MM-DD');
      if (filters.branchId) params.branchId = filters.branchId;
      
      const res = await fetchCentralFund(params);
      const data = res?.data;
      
      setEntries(data?.data || []);
      setPagination(prev => ({
        ...prev,
        current: data?.pagination?.current || 1,
        total: data?.pagination?.total || 0,
      }));
    } catch (err) {
      setError(err.message || 'Failed to load branch funding history');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.pageSize]);

  useEffect(() => {
    if (!initLoading && isHO) fetchHistory(1);
  }, [filters, initLoading, isHO]);

  const columns = [
    {
      title: 'Date',
      dataIndex: 'createdAt',
      width: 120,
      render: (v) => v ? dayjs(v).format('DD/MM/YYYY') : '—',
    },
    {
      title: 'Branch',
      dataIndex: ['branch', 'name'],
      width: 180,
      render: (v, row) => row.branch?.name || <Text type="secondary">—</Text>,
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      width: 150,
      render: (v) => <Text code style={{ fontSize: 12 }}>{v}</Text>,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      width: 150,
      align: 'right',
      render: (v) => <Text strong style={{ color: '#389e0d' }}>₹{fmt(v)}</Text>,
    },
    {
      title: 'Funded By',
      dataIndex: ['createdBy', 'name'],
      width: 150,
      render: (v, row) => row.createdBy?.name || <Text type="secondary">—</Text>,
    },
    {
      title: 'Remarks',
      dataIndex: 'description',
      ellipsis: true,
    },
  ];

  if (initLoading) return <Spin style={{ display: 'block', marginTop: 80 }} />;

  if (!isHO) {
    return (
      <div style={{ padding: '80px 0', textAlign: 'center' }}>
        <LockOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
        <Title level={5} type="secondary">Branch Funding History</Title>
        <Text type="secondary">This section is restricted to Head Office users.</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 0' }}>
      {/* ── Header ── */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}>
            <FileTextOutlined style={{ marginRight: 8 }} />
            Branch Cash Funding History
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Record of all funds allocated to branches from Central Fund</Text>
        </Col>
        <Col>
          <Tooltip title="Refresh">
            <Button icon={<ReloadOutlined />} onClick={() => fetchHistory(1)} loading={loading} />
          </Tooltip>
        </Col>
      </Row>

      {error && (
        <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />
      )}

      {/* ── Filters ── */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 8]} align="middle">
          <Col xs={24} sm={8}>
            <Select
              placeholder="Select Branch"
              allowClear
              showSearch
              optionFilterProp="children"
              style={{ width: '100%' }}
              value={filters.branchId}
              onChange={(v) => setFilters(f => ({ ...f, branchId: v || null }))}
              loading={branchesLoading}
            >
              {branches.map(b => (
                <Select.Option key={b._id} value={b._id}>
                  {b.name} ({b.code})
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={10}>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.dateRange}
              onChange={(dates) => setFilters(f => ({ ...f, dateRange: dates }))}
              format="DD/MM/YYYY"
            />
          </Col>
        </Row>
      </Card>

      {/* ── Table ── */}
      <Table
        dataSource={entries}
        columns={columns}
        rowKey="_id"
        loading={loading}
        size="small"
        scroll={{ x: 1000 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (t) => `${t} entries`,
          onChange: (page) => fetchHistory(page),
        }}
        locale={{ emptyText: 'No branch funding history found' }}
      />
    </div>
  );
}
